import { useState, useEffect, useMemo } from 'react';
import { ArrowRight, X, Share2, Check } from 'lucide-react';
import { PATES_PHOTOS } from '../data/photos';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Données statiques (fallback si API inaccessible) ──────────────────────────
const STATIC_STEPS = [
  {
    label: "1 Type de pâtes fraîches",
    sub: "4 types de pâte fraîche au choix",
    products: [
      { name: "Fusilli — Fondante" },
      { name: "Campanelle — Fondante" },
      { name: "Mafaldine — Al dente" },
      { name: "Radiatori — Al dente" },
    ],
  },
  {
    label: "1 Sauce",
    sub: "6 sauces maison",
    products: [
      { name: "Carbo Grainette" }, { name: "Bolognaise" },
      { name: "Crème de cèpes & poulet" }, { name: "Crème de saumon & ricotta" },
      { name: "Pesto rosso" }, { name: "Crème de truffe" },
    ],
  },
  {
    label: "1 Condiment",
    sub: "6 condiments",
    products: [
      { name: "Parmesan râpé" }, { name: "Emmental râpé" },
      { name: "Pistaches torréfiées" }, { name: "Pignons torréfiés" },
      { name: "Noisettes torréfiées" }, { name: "Noix" },
    ],
  },
  {
    label: "Des Extras",
    sub: "En option",
    categories: [
      {
        name: "+ 0,50 €",
        color: "#EEB52D",
        items: [
          { name: "Parmesan râpé" }, { name: "Emmental râpé" },
          { name: "Pistaches torréfiées" }, { name: "Pignons torréfiés" },
          { name: "Noisettes torréfiées" }, { name: "Noix" },
        ],
      },
      {
        name: "+ 1,00 €",
        color: "#e86026",
        items: [
          { name: "Olives de Kalamata" }, { name: "Tomates confites" },
          { name: "Pâté de champignons" }, { name: "Jambon blanc" },
          { name: "Ricotta" }, { name: "Mozzarella di Bufala" },
        ],
      },
      {
        name: "+ 1,50 €",
        color: "#2A3630",
        items: [
          { name: "Chorizo doux grillé" }, { name: "Coppa grillée" },
          { name: "Stracciatella" },
        ],
      },
    ],
  },
];

// ── Transformation données API → format STEPS ─────────────────────────────────
function buildPatesSteps(items) {
  if (!items || items.length === 0) return null;

  const byCategory = items.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {});

  const types = byCategory.types || [];
  const sauces = byCategory.sauces || [];
  const condiments = byCategory.condiments || [];
  const extras = byCategory.extras || [];

  if (!types.length && !sauces.length) return null;

  const priceColorMap = { 0.5: '#EEB52D', 1.0: '#e86026', 1.5: '#2A3630' };
  const priceGroups = {};
  extras.forEach(item => {
    const p = item.price || 0;
    if (!priceGroups[p]) priceGroups[p] = [];
    priceGroups[p].push({ name: item.name });
  });

  const extraCategories = Object.entries(priceGroups)
    .sort(([a], [b]) => parseFloat(a) - parseFloat(b))
    .map(([price, its]) => ({
      name: `+ ${parseFloat(price).toFixed(2).replace('.', ',')} €`,
      color: priceColorMap[parseFloat(price)] || '#2A3630',
      items: its,
    }));

  return [
    {
      label: '1 Type de pâtes fraîches',
      sub: `${types.length} type${types.length > 1 ? 's' : ''} de pâte fraîche au choix`,
      products: types.map(i => ({
        name: i.description
          ? `${i.name} — ${i.description.charAt(0).toUpperCase() + i.description.slice(1)}`
          : i.name,
      })),
    },
    {
      label: '1 Sauce',
      sub: `${sauces.length} sauce${sauces.length > 1 ? 's' : ''} maison`,
      products: sauces.map(i => ({ name: i.name })),
    },
    {
      label: '1 Condiment',
      sub: `${condiments.length} condiment${condiments.length > 1 ? 's' : ''}`,
      products: condiments.map(i => ({ name: i.name })),
    },
    {
      label: 'Des Extras',
      sub: 'En option',
      categories: extraCategories,
    },
  ];
}

export default function MenuPates() {
  const [steps, setSteps] = useState(STATIC_STEPS);
  const [active, setActive] = useState(null);
  const [shareState, setShareState] = useState('idle');
  const heroPhotos = useMemo(() => [...PATES_PHOTOS].sort(() => Math.random() - 0.5).slice(0, 3), []);

  useEffect(() => {
    fetch(`${API}/api/menu/pates`)
      .then(r => r.ok ? r.json() : [])
      .then(items => {
        const built = buildPatesSteps(items);
        if (built) setSteps(built);
      })
      .catch(() => {}); // fallback silencieux sur les données statiques
  }, []);

  const handleShare = async () => {
    const url = window.location.origin + '/menus-pates';
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Menu Fabrique à Pâtes — Grainette Paris 9e',
          text: 'Pâtes fraîches fabriquées sur place chaque matin. Fabrique à pâtes Paris 9e.',
          url,
        });
      } catch (e) { /* annulé par l'utilisateur */ }
    } else {
      await navigator.clipboard.writeText(url);
      setShareState('copied');
      setTimeout(() => setShareState('idle'), 2200);
    }
  };

  return (
    <div className="bg-[#FAFAFA] min-h-screen pt-16" data-testid="menu-pates-page">

      {/* HEADER — 3 photos côte à côte */}
      <div className="relative overflow-hidden" style={{ minHeight: '440px' }} data-testid="pates-hero">
        <div className="absolute inset-0 flex">
          {heroPhotos.map((photo, i) => (
            <div
              key={i}
              className="flex-1 h-full"
              style={{
                backgroundImage: `url(${photo})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
          ))}
        </div>
        <div className="absolute inset-0" style={{ backgroundColor: '#7A5200', opacity: 0.60 }} />
        <div className="relative flex items-center justify-center py-24 px-4" style={{ zIndex: 2 }}>
          <div className="max-w-4xl mx-auto text-center text-white">
            <img
              src="https://customer-assets.emergentagent.com/job_grainette-migration/artifacts/i2pxliea__Grainette%20_%20.png"
              alt="Grainette"
              className="w-auto object-contain mx-auto mb-4"
              style={{ height: '11rem', filter: 'brightness(0) invert(1)' }}
            />
            <h1 className="text-5xl sm:text-6xl font-black mb-3" style={{ fontFamily: 'Barlow, sans-serif' }}>
              Fabrique à Pâtes
            </h1>
            <p className="text-white/90 text-base max-w-xl mx-auto mb-6">
              Pâtes et sauces fraîches fabriquées sur place chaque matin, composées devant vous avec des ingrédients de qualité
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a href="https://app.pulp.eu/shop/fee3a523-042e-480b-bd3b-6446dfde76a7?gl=1&free=true&utm_source=site&utm_campaign=GrainetteParis9"
                target="_blank" rel="noopener noreferrer"
                data-testid="order-pates-btn"
                className="inline-flex items-center gap-2 px-8 py-3 bg-white font-bold rounded-full text-sm btn-lift"
                style={{ color: '#EEB52D' }}>
                Commander en ligne <ArrowRight size={14} />
              </a>

              <button
                onClick={handleShare}
                data-testid="share-pates-btn"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold transition-all duration-200"
                style={{
                  backgroundColor: shareState === 'copied' ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.15)',
                  color: '#fff',
                  border: '1.5px solid rgba(255,255,255,0.4)',
                  backdropFilter: 'blur(8px)',
                }}
              >
                {shareState === 'copied'
                  ? <><Check size={14} /> Lien copié !</>
                  : <><Share2 size={14} /> Partager</>}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Panneau menu physique */}
      <section className="py-14 px-6 bg-[#F5F5F5]" data-testid="menus-pates-section">
        <div className="max-w-6xl mx-auto flex justify-center">
          <div className="rounded-2xl overflow-hidden shadow-md border border-gray-100 w-full max-w-sm">
            <img
              src="/menu-boards/board-pates-web.jpg"
              alt="Panneau menu Pâtes Grainette"
              className="w-full h-auto"
              loading="lazy"
              decoding="async"
              data-testid="pates-board-img"
            />
          </div>
        </div>
      </section>

      {/* COMMENT COMPOSER */}
      <section className="py-14 px-6 bg-[#F5F5F5]" data-testid="concept-pates-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-4xl sm:text-5xl font-black"
              style={{ fontFamily: 'Barlow, sans-serif', color: '#2A3630' }}>
              Comment composer vos pâtes ?
            </h2>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch justify-center gap-0">
            {steps.map((step, i) => (
              <div key={step.label} className="flex flex-col sm:flex-row items-center">
                <div
                  onClick={() => setActive(active === i ? null : i)}
                  data-testid={`concept-pates-step-${i + 1}`}
                  className="flex flex-col items-center text-center px-4 py-5 rounded-2xl border border-gray-200 cursor-pointer select-none"
                  style={{
                    backgroundColor: active === i ? '#EEB52D' : 'white',
                    width: '160px',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease',
                    transform: active === i ? 'scale(1.08)' : 'scale(1)',
                    boxShadow: active === i ? '0 8px 24px rgba(238,181,45,0.3)' : '0 1px 4px rgba(0,0,0,0.06)',
                  }}
                  onMouseEnter={e => { if (active !== i) { e.currentTarget.style.transform = 'scale(1.05)'; e.currentTarget.style.boxShadow = '0 6px 18px rgba(0,0,0,0.1)'; }}}
                  onMouseLeave={e => { if (active !== i) { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.06)'; }}}
                >
                  <p className="font-black text-sm mb-0.5"
                    style={{ fontFamily: 'Barlow, sans-serif', color: active === i ? 'white' : '#2A3630' }}>
                    {step.label}
                  </p>
                  <p className="text-xs" style={{ color: active === i ? 'rgba(255,255,255,0.8)' : '#9ca3af', fontFamily: 'Poppins, sans-serif' }}>
                    {step.sub}
                  </p>
                </div>

                {i < steps.length - 1 && (
                  <svg viewBox="0 0 80 80" width="32" height="32" className="shrink-0 mx-0.5 my-1 sm:my-0">
                    <defs>
                      <filter id={`rough-p-${i}`} x="-15%" y="-15%" width="130%" height="130%">
                        <feTurbulence type="fractalNoise" baseFrequency="0.07" numOctaves="4" seed={i + 5} result="noise"/>
                        <feDisplacementMap in="SourceGraphic" in2="noise" scale="3" xChannelSelector="R" yChannelSelector="G"/>
                      </filter>
                    </defs>
                    <path d="M29,4 H51 V29 H76 V51 H51 V76 H29 V51 H4 V29 H29 Z"
                      fill="#EEB52D" filter={`url(#rough-p-${i})`}/>
                  </svg>
                )}
              </div>
            ))}
          </div>

          {/* Panneau détail */}
          {active !== null && (steps[active].products?.length > 0 || steps[active].categories) && (
            <div className="mt-8 rounded-2xl border border-gray-100 bg-white p-6 shadow-sm"
              style={{ animation: 'fadeIn 0.2s ease' }}
              data-testid="concept-pates-detail-panel">
              <div className="flex items-start justify-between mb-5">
                <div>
                  <h3 className="text-2xl font-black" style={{ fontFamily: 'Barlow, sans-serif', color: '#2A3630' }}>
                    {steps[active].label}
                  </h3>
                  <p className="text-sm text-gray-400 mt-1" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {steps[active].sub}
                  </p>
                </div>
                <button onClick={() => setActive(null)} className="text-gray-400 hover:text-gray-700 transition-colors p-1">
                  <X size={20} />
                </button>
              </div>

              {steps[active].products && (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                  {steps[active].products.map((p) => (
                    <div key={p.name} className="relative px-3 py-2 rounded-xl text-center text-sm font-medium"
                      style={{ backgroundColor: 'rgba(238,181,45,0.1)', color: '#2A3630', fontFamily: 'Poppins, sans-serif' }}>
                      {p.hot && (
                        <span className="absolute -top-2 -right-1 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full leading-none"
                          style={{ backgroundColor: '#e86026' }}>
                          chaud
                        </span>
                      )}
                      {p.name}
                    </div>
                  ))}
                </div>
              )}

              {steps[active].categories && (
                <div className="space-y-6">
                  {steps[active].categories.map((cat) => (
                    <div key={cat.name}>
                      <h4 className="font-black text-sm mb-3 uppercase tracking-wider"
                        style={{ fontFamily: 'Barlow, sans-serif', color: cat.color }}>
                        {cat.name}
                      </h4>
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                        {cat.items.map((item) => (
                          <div key={item.name} className="relative px-3 py-2 rounded-xl text-center text-xs font-medium"
                            style={{ backgroundColor: `${cat.color}18`, color: '#2A3630', fontFamily: 'Poppins, sans-serif' }}>
                            {item.hot && (
                              <span className="absolute -top-2 -right-1 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full leading-none"
                                style={{ backgroundColor: '#e86026' }}>
                                chaud
                              </span>
                            )}
                            {item.name}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* CTA Commander */}
      <section className="py-16 px-6 bg-[#F5F5F5] border-t border-gray-100">
        <div className="max-w-xl mx-auto text-center">
          <h2 className="text-3xl font-black mb-3" style={{ fontFamily: 'Barlow, sans-serif', color: '#2A3630' }}>
            Envie de pâtes ?
          </h2>
          <p className="text-gray-500 text-sm mb-8">Commandez en ligne pour récupérer vos pâtes sans attendre.</p>
          <a
            href="https://commandes.grainette.fr/home/4404/2/menu"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="cta-order-pates"
            className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full font-semibold text-white text-sm btn-lift"
            style={{ backgroundColor: '#EEB52D' }}
          >
            Commander — Godot de Mauroy
          </a>
        </div>
      </section>

    </div>
  );
}
